import * as React from 'react';
import { useState } from 'react';

interface Member {
  id: number;
  name: string;
  gender: 'male' | 'female';
  birthYear: number;
  isAlive: boolean;
  deathYear?: number;
  parentIds: number[];
}

interface Relationship {
  id: number;
  type: 'marriage' | 'separation' | 'divorce' | 'engagement';
  person1Id: number;
  person2Id: number;
}

interface FamilyUnit {
  type: 'couple' | 'individual';
  members?: [Member, Member];
  member?: Member;
  children: FamilyUnit[];
  parents?: Member[];
  id?: number;
}

interface MemberCardProps {
  member: Member;
  size?: 'small' | 'normal' | 'large';
  isSelected?: boolean;
}

interface Position {
  x: number;
  y: number;
}

interface TreeNodePosition {
  unit: FamilyUnit;
  position: Position;
  width: number;
  height: number;
}

const FamilyTreeBuilder: React.FC = () => {
  // State for family members
  const [members, setMembers] = useState<Member[]>([
    { id: 1, name: 'John Smith', gender: 'male', birthYear: 1960, isAlive: true, deathYear: undefined, parentIds: [] },
    { id: 2, name: 'Jane Smith', gender: 'female', birthYear: 1962, isAlive: true, deathYear: undefined, parentIds: [] },
    { id: 3, name: 'Michael Smith', gender: 'male', birthYear: 1985, isAlive: true, deathYear: undefined, parentIds: [1, 2] },
    { id: 4, name: 'Sarah Johnson', gender: 'female', birthYear: 1987, isAlive: true, deathYear: undefined, parentIds: [] },
    { id: 5, name: 'Emma Smith', gender: 'female', birthYear: 2010, isAlive: true, deathYear: undefined, parentIds: [3, 4] },
    { id: 6, name: 'Jacob Smith', gender: 'male', birthYear: 2012, isAlive: true, deathYear: undefined, parentIds: [3, 4] }
  ]);
  
  // State for relationships
  const [relationships, setRelationships] = useState<Relationship[]>([
    { id: 1, type: 'marriage', person1Id: 1, person2Id: 2 },
    { id: 2, type: 'marriage', person1Id: 3, person2Id: 4 }
  ]);
  
  // State for form input
  const [newMember, setNewMember] = useState<Member>({
    id: 0,
    name: '',
    gender: 'male',
    birthYear: 2000,
    isAlive: true,
    deathYear: undefined,
    parentIds: []
  });
  
  // State for member editing
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // State for selected members
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  
  // State for relationship type
  const [relationshipType, setRelationshipType] = useState<Relationship['type']>('marriage');

  // State for view mode
  const [viewMode, setViewMode] = useState<'cards' | 'tree'>('cards');

  // Function to add new member
  const addMember = () => {
    if (newMember.name.trim() === '') return;
    
    const newId = members.length > 0 ? Math.max(...members.map(m => m.id)) + 1 : 1;
    
    setMembers([...members, {
      ...newMember,
      id: newId
    }]);
    
    // Reset form
    setNewMember({
      id: 0,
      name: '',
      gender: 'male',
      birthYear: 2000,
      isAlive: true,
      deathYear: undefined,
      parentIds: []
    });
  };
  
  // Function to start editing a member
  const startEditMember = (member: Member) => {
    setEditingMember({ ...member });
    setIsEditing(true);
  };
  
  // Function to save edited member
  const saveEditMember = () => {
    if (!editingMember || editingMember.name.trim() === '') return;
    
    setMembers(members.map(member => 
      member.id === editingMember.id ? editingMember : member
    ));
    
    setIsEditing(false);
    setEditingMember(null);
  };
  
  // Function to cancel editing
  const cancelEdit = () => {
    setIsEditing(false);
    setEditingMember(null);
  };
  
  // Function to delete a member
  const deleteMember = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this family member?")) return;
    
    // Remove member
    setMembers(prevMembers => prevMembers.filter(member => member.id !== id));
    
    // Remove relationships involving this member
    setRelationships(prevRelationships => prevRelationships.filter(rel => 
      rel.person1Id !== id && rel.person2Id !== id
    ));
    
    // Remove this member from parent references
    setMembers(prevMembers => prevMembers.map(member => ({
      ...member,
      parentIds: member.parentIds.filter(parentId => parentId !== id)
    })));
    
    // Clear selection if this member was selected
    setSelectedMembers(prevSelected => prevSelected.filter(memberId => memberId !== id));
    
    // If we're editing this member, cancel editing
    if (editingMember && editingMember.id === id) {
      setIsEditing(false);
      setEditingMember(null);
    }
  };
  
  // Function to handle member selection
  const toggleSelectMember = (id: number) => {
    if (selectedMembers.includes(id)) {
      setSelectedMembers(selectedMembers.filter(memberId => memberId !== id));
    } else {
      if (selectedMembers.length < 2) {
        setSelectedMembers([...selectedMembers, id]);
      }
    }
  };
  
  // Function to create relationship
  const createRelationship = () => {
    if (selectedMembers.length !== 2) return;
    
    const newRelationshipId = relationships.length > 0 ? 
      Math.max(...relationships.map(r => r.id)) + 1 : 1;
    
    setRelationships([...relationships, {
      id: newRelationshipId,
      type: relationshipType,
      person1Id: selectedMembers[0],
      person2Id: selectedMembers[1]
    }]);
    
    // Reset selection
    setSelectedMembers([]);
  };
  
  // Get parent names for a member
  const getParentNames = (parentIds: number[]): string => {
    return parentIds.map(id => {
      const parent = members.find(m => m.id === id);
      return parent ? parent.name : 'Unknown';
    }).join(', ');
  };
  
  // Get relationship info
  const getRelationshipInfo = (memberId: number): string => {
    const rels = relationships.filter(r => 
      r.person1Id === memberId || r.person2Id === memberId
    );
    
    return rels.map(rel => {
      const partnerId = rel.person1Id === memberId ? rel.person2Id : rel.person1Id;
      const partner = members.find(m => m.id === partnerId);
      return partner ? `${rel.type === 'marriage' ? 'Married to' : 'Related to'} ${partner.name}` : '';
    }).join(', ');
  };

  // Function to find partner for a member
  const findPartner = (memberId: number): Member | null => {
    const rel = relationships.find(r => 
      (r.person1Id === memberId || r.person2Id === memberId) && 
      r.type === 'marriage'
    );
    
    if (!rel) return null;
    
    const partnerId = rel.person1Id === memberId ? rel.person2Id : rel.person1Id;
    return members.find(m => m.id === partnerId) || null;
  };

  // Find children for a person
  const findChildren = (memberId: number): Member[] => {
    return members.filter(member => 
      member.parentIds.includes(memberId)
    );
  };

  // Get children for a couple
  const getChildrenForCouple = (person1Id: number, person2Id: number): Member[] => {
    return members.filter(member => 
      member.parentIds.includes(person1Id) && member.parentIds.includes(person2Id)
    );
  };

  // Member Card Component
  const MemberCard: React.FC<MemberCardProps> = ({ member, size = 'normal', isSelected = false }) => {
    const partner = findPartner(member.id);
    
    let cardClass = "member-card ";
    if (isSelected) cardClass += "selected ";
    // Add gender-specific background classes
    cardClass += member.gender === 'male' ? "member-card-male " : "member-card-female ";
    
    let sizeClass = "";
    switch (size) {
      case 'small':
        sizeClass = "p-4 text-sm";
        break;
      case 'large':
        sizeClass = "p-6";
        break;
      default:
        sizeClass = "p-5";
    }
    
    return (
      <div 
        className={`${cardClass} ${sizeClass} rounded-xl shadow-md`}
        style={size === 'small' ? { height: '120px', minHeight: '120px' } : undefined}
        onClick={() => toggleSelectMember(member.id)}
      >
        <div className="flex items-center mb-4">
          <div 
            className={`w-16 h-16 rounded-full mr-4 flex items-center justify-center text-white text-xl ${member.gender === 'male' ? 'gender-icon-male' : 'gender-icon-female'}`}
          >
            {member.gender === 'male' ? 'M' : 'F'}
          </div>
          <div>
            <h3 className="md-title-medium mb-1">{member.name}</h3>
            <p className="md-body-medium text-gray-600">
              {member.birthYear} - {member.isAlive ? 'Present' : member.deathYear || 'Deceased'}
            </p>
          </div>
        </div>
        
        {size !== 'small' && (
          <>
            {member.parentIds.length > 0 && (
              <p className="md-body-medium mb-3">
                <span className="font-semibold">Parents:</span> {getParentNames(member.parentIds)}
              </p>
            )}
            
            {partner && (
              <p className="md-body-medium mb-3">
                <span className="font-semibold">Spouse:</span> {partner.name}
              </p>
            )}
            
            <div className="flex mt-5 justify-end button-group">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  startEditMember(member);
                }}
                className="md-button md-button-filled"
              >
                Edit
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  deleteMember(member.id);
                }}
                className="md-button md-button-outlined"
              >
                Delete
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Function to find a family unit by parent ID
  const findFamilyUnitByParent = (parentId: number): FamilyUnit | null => {
    for (const unit of buildFamilyTree()) {
      if ((unit.type === 'individual' && unit.member?.id === parentId) || 
          (unit.type === 'couple' && unit.members?.some(m => m.id === parentId))) {
        return unit;
      }
    }
    return null;
  };

  // Build family tree structure
  const buildFamilyTree = (): FamilyUnit[] => {
    const result: FamilyUnit[] = [];
    // Track processed members to avoid duplicates
    const processedMemberIds = new Set<number>();
    // Track processed couples to avoid duplicates
    const processedCoupleIds = new Set<string>();
    // Map to store all created units by member id for quick lookup
    const memberUnitMap = new Map<number, FamilyUnit>();
    // Map to store parent unit by child ids (for grouping siblings)
    const childParentMap = new Map<number, FamilyUnit>();
    
    // First pass: Process all couples
    for (const rel of relationships) {
      if (rel.type === 'marriage') {
        const person1 = members.find(m => m.id === rel.person1Id);
        const person2 = members.find(m => m.id === rel.person2Id);
        
        if (person1 && person2) {
          // Create a unique key for this couple
          const coupleKey = `${Math.min(person1.id, person2.id)}-${Math.max(person1.id, person2.id)}`;
          
          // Skip if we've already processed this couple
          if (processedCoupleIds.has(coupleKey)) continue;
          processedCoupleIds.add(coupleKey);
          
          // Mark both members as processed
          processedMemberIds.add(person1.id);
          processedMemberIds.add(person2.id);
          
          // Create empty children array which we'll populate in second pass
          const coupleUnit: FamilyUnit = {
            type: 'couple',
            members: [person1, person2],
            children: [],
            id: person1.id // Using first person's ID as unit ID
          };
          
          // Add to map for both members
          memberUnitMap.set(person1.id, coupleUnit);
          memberUnitMap.set(person2.id, coupleUnit);
          
          // Add to result only if they are root-level (no parents in system)
          const hasNoParents = (person: Member) => 
            person.parentIds.length === 0 || 
            person.parentIds.every(pid => !members.some(m => m.id === pid));
            
          if (hasNoParents(person1) && hasNoParents(person2)) {
            result.push(coupleUnit);
          }
          
          // Find their children - preprocess for grouping
          const commonChildren = members.filter(m => 
            m.parentIds.includes(person1.id) && m.parentIds.includes(person2.id)
          );
          
          // Map each child to this parent unit
          commonChildren.forEach(child => {
            childParentMap.set(child.id, coupleUnit);
          });
        }
      }
    }
    
    // Second pass: Process all individuals not in couples
    for (const member of members) {
      // Skip if already processed (as part of a couple)
      if (processedMemberIds.has(member.id)) continue;
      
      processedMemberIds.add(member.id);
      
      const individualUnit: FamilyUnit = {
        type: 'individual',
        member: member,
        children: [],
        id: member.id
      };
      
      memberUnitMap.set(member.id, individualUnit);
      
      // Add to result only if root-level
      if (member.parentIds.length === 0 || 
          member.parentIds.every(pid => !members.some(m => m.id === pid))) {
        result.push(individualUnit);
      } else {
        // Not a root - try to find parent unit
        const parentUnit = member.parentIds
          .map(pid => memberUnitMap.get(pid))
          .filter(Boolean)[0];
          
        if (parentUnit) {
          childParentMap.set(member.id, parentUnit);
        }
      }
    }
    
    // Process children by families - important for grouping siblings
    const familyChildren = new Map<number, Member[]>();
    
    // Group children by their parent unit ID
    members.forEach(member => {
      if (member.parentIds.length === 0) return; // Skip root members
      
      const parentUnit = childParentMap.get(member.id);
      if (!parentUnit || parentUnit.id === undefined) return; // Skip if no parent unit found
      
      // Initialize array if needed
      if (!familyChildren.has(parentUnit.id)) {
        familyChildren.set(parentUnit.id, []);
      }
      
      // Add to family group
      const childrenArray = familyChildren.get(parentUnit.id);
      if (childrenArray) {
        childrenArray.push(member);
      }
    });
    
    // Add children to their parent units all at once (keeps siblings together)
    familyChildren.forEach((childGroup, parentUnitId) => {
      const parentUnit = Array.from(memberUnitMap.values())
        .find(unit => unit.id === parentUnitId);
      
      if (!parentUnit) return;
      
      // Add each child as individual unit
      childGroup.forEach(child => {
        // Skip if this child is already added
        if (parentUnit.children.some(c => 
            (c.type === 'individual' && c.member?.id === child.id) ||
            (c.type === 'couple' && c.members?.some(m => m.id === child.id))
        )) {
          return;
        }
        
        // See if this child is part of a couple
        const childUnit = memberUnitMap.get(child.id);
        if (childUnit) {
          // Add the existing unit
          parentUnit.children.push(childUnit);
        } else {
          // Create new individual unit
          const newUnit: FamilyUnit = {
            type: 'individual',
            member: child,
            children: [],
            id: child.id
          };
          memberUnitMap.set(child.id, newUnit);
          parentUnit.children.push(newUnit);
        }
      });
    });
    
    // Sort children within each unit by birth year if available
    const sortChildren = (unit: FamilyUnit) => {
      // Sort children by birth year
      unit.children.sort((a, b) => {
        const getBirthYear = (u: FamilyUnit): number => {
          if (u.type === 'individual' && u.member) {
            return u.member.birthYear || 0;
          } else if (u.type === 'couple' && u.members && u.members.length > 0) {
            return u.members[0].birthYear || 0;
          }
          return 0;
        };
        
        return getBirthYear(a) - getBirthYear(b);
      });
      
      // Recursively sort grandchildren
      unit.children.forEach(sortChildren);
    };
    
    // Sort all root units
    result.forEach(sortChildren);
    
    return result;
  };
  
  // Add this new component for canvas-based tree view
  const CanvasTreeView: React.FC<{ familyUnits: FamilyUnit[] }> = ({ familyUnits }) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [positions, setPositions] = React.useState<TreeNodePosition[]>([]);
    const [canvasSize, setCanvasSize] = React.useState({ width: 3000, height: 2000 });
    const [offset, setOffset] = React.useState({ x: 0, y: 0 });
    
    // Updated spacing constants
    const CARD_WIDTH = 200;
    const CARD_HEIGHT = 120;  // Set to exactly 120px
    const VERTICAL_SPACING = 240;  // Increased from 160 to 240 for more vertical space
    const HORIZONTAL_SPACING = 60;
    const COUPLE_GAP = 40;
    const SIBLING_GAP = 80;

    // Calculate positions for all nodes
    const calculatePositions = React.useCallback(() => {
      const newPositions: TreeNodePosition[] = [];
      let maxWidth = 0;
      let maxHeight = 0;

      const processUnit = (unit: FamilyUnit, x: number, y: number, level: number) => {
        // Calculate width needed for this unit and its children
        const unitWidth = unit.type === 'couple' ? 
          CARD_WIDTH * 2 + COUPLE_GAP : // Width for couples
          CARD_WIDTH; // Width for individuals
        
        // Add this unit to positions
        newPositions.push({
          unit,
          position: { x, y },
          width: unitWidth,
          height: CARD_HEIGHT
        });

        maxWidth = Math.max(maxWidth, x + unitWidth);
        maxHeight = Math.max(maxHeight, y + CARD_HEIGHT);

        // Process children
        if (unit.children.length > 0) {
          const totalChildrenWidth = unit.children.reduce((total, child, index) => {
            // Add sibling gap for all but the first child
            const siblingSpace = index > 0 ? SIBLING_GAP : 0;
            return total + siblingSpace + (child.type === 'couple' ? 
              CARD_WIDTH * 2 + COUPLE_GAP : // Width for couple child
              CARD_WIDTH); // Width for individual child
          }, 0);

          // Calculate starting X position for first child to center all children
          let childX = x + (unitWidth - totalChildrenWidth) / 2;
          const childY = y + CARD_HEIGHT + VERTICAL_SPACING;

          unit.children.forEach((child, index) => {
            // Add sibling gap after the first child
            if (index > 0) {
              childX += SIBLING_GAP;
            }
            
            processUnit(child, childX, childY, level + 1);
            childX += child.type === 'couple' ? 
              CARD_WIDTH * 2 + COUPLE_GAP : // Move pointer past couple
              CARD_WIDTH; // Move pointer past individual
          });
        }
      };

      // Start processing from root units with proper spacing
      let currentX = HORIZONTAL_SPACING;
      familyUnits.forEach((unit, index) => {
        // Add extra spacing between root units
        if (index > 0) {
          currentX += SIBLING_GAP;
        }
        
        processUnit(unit, currentX, VERTICAL_SPACING, 0);
        currentX += unit.type === 'couple' ? 
          CARD_WIDTH * 2 + COUPLE_GAP : // Move pointer past couple
          CARD_WIDTH; // Move pointer past individual
      });

      setCanvasSize({
        width: Math.max(3000, maxWidth + HORIZONTAL_SPACING * 2),
        height: Math.max(2000, maxHeight + VERTICAL_SPACING * 2)
      });
      setPositions(newPositions);
    }, [familyUnits]);

    // Initialize positions
    React.useEffect(() => {
      calculatePositions();
    }, [calculatePositions]);

    // Handle scrolling
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      setOffset({
        x: container.scrollLeft,
        y: container.scrollTop
      });
    };

    return (
      <div 
        ref={containerRef}
        style={{
          width: '100%',
          height: '800px',
          overflow: 'auto',
          position: 'relative',
          border: '1px solid #ccc',
          borderRadius: '8px'
        }}
        onScroll={handleScroll}
      >
        <div style={{
          width: canvasSize.width,
          height: canvasSize.height,
          position: 'relative'
        }}>
          {/* Draw connection lines */}
          <svg 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none'
            }}
          >
            {positions.map(node => {
              if (node.unit.children.length > 0) {
                const childPositions = node.unit.children.map(child => 
                  positions.find(p => p.unit === child)
                ).filter(Boolean) as TreeNodePosition[];

                return childPositions.map((childPos, index) => {
                  // Calculate the start point based on whether the node is a couple or individual
                  let startX;
                  if (node.unit.type === 'couple' && node.unit.members) {
                    // For couples, start from between the two cards
                    startX = node.position.x + CARD_WIDTH + COUPLE_GAP / 2;
                  } else {
                    // For individuals, start from the middle of the card
                    startX = node.position.x + node.width / 2;
                  }
                  
                  // Calculate the start point Y-coordinate
                  // We need 280. node.position.y is 240, CARD_HEIGHT is 120.
                  // Calculation: 240 + 120 = 360. To get 280, subtract 80.
                  const parentY = node.position.y + CARD_HEIGHT - 80; 
                  
                  const childY = childPos.position.y;
                  const midPointY = parentY + (childY - parentY) / 2;
                  
                  // Calculate the end point
                  let endX;
                  if (childPos.unit.type === 'couple' && childPos.unit.members) {
                    // For couples, end at between the two cards
                    endX = childPos.position.x + CARD_WIDTH + COUPLE_GAP / 2;
                  } else {
                    // For individuals, end at the middle of the card
                    endX = childPos.position.x + childPos.width / 2;
                  }
                  
                  // Log the values right before rendering the path
                  console.log(`Rendering path for Node ${node.unit.id} -> Child ${childPos.unit.id}: startX=${startX}, parentY=${parentY}, endX=${endX}, childY=${childY}, midPointY=${midPointY}`);
                  console.log(` -> Used node.position.y=${node.position.y}, CARD_HEIGHT=${CARD_HEIGHT}`);

                  // Remove debug special case now that we've fixed the calculation
                  // Use the same formula for all paths with correct calculation
                  return (
                    <path
                      key={`${node.unit.id}-${index}`}
                      d={`M ${startX} ${parentY} 
                         L ${startX} ${midPointY}
                         L ${endX} ${midPointY}
                         L ${endX} ${childY}`}
                      stroke="#999"
                      strokeWidth="2"
                      fill="none"
                    />
                  );
                });
              }
              return null;
            })}
          </svg>

          {/* Render nodes */}
          {positions.map((node, index) => (
            <div
              key={`${node.unit.id || index}`}
              style={{
                position: 'absolute',
                left: node.position.x,
                top: node.position.y,
                display: 'flex',
                gap: HORIZONTAL_SPACING
              }}
            >
              {node.unit.type === 'couple' && node.unit.members ? (
                <>
                  <MemberCard 
                    member={node.unit.members[0]} 
                    size="small"
                    isSelected={selectedMembers.includes(node.unit.members[0].id)}
                  />
                  <MemberCard 
                    member={node.unit.members[1]} 
                    size="small"
                    isSelected={selectedMembers.includes(node.unit.members[1].id)}
                  />
                </>
              ) : node.unit.member ? (
                <MemberCard 
                  member={node.unit.member} 
                  size="small"
                  isSelected={selectedMembers.includes(node.unit.member.id)}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="family-tree-builder p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div className="button-group">
          <button 
            onClick={() => setViewMode('cards')}
            className={`md-button ${viewMode === 'cards' ? 'md-button-filled' : 'md-button-outlined'} rounded-l-md rounded-r-none`}
          >
            Cards
          </button>
          <button 
            onClick={() => setViewMode('tree')}
            className={`md-button ${viewMode === 'tree' ? 'md-button-filled' : 'md-button-outlined'} rounded-r-md rounded-l-none`}
          >
            Tree View
          </button>
        </div>
      </div>
      
      {/* Add New Member Form */}
      <div className="bg-gray-100 p-6 rounded-lg mb-8 section">
        <h2 className="md-title-large mb-6">{isEditing ? 'Edit Member' : 'Add New Family Member'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md-text-field">
            <input 
              type="text" 
              id="member-name"
              value={isEditing ? editingMember?.name : newMember.name}
              onChange={(e) => {
                if (isEditing && editingMember) {
                  setEditingMember({...editingMember, name: e.target.value});
                } else {
                  setNewMember({...newMember, name: e.target.value});
                }
              }}
            />
            <label htmlFor="member-name">Name</label>
          </div>
          
          <div className="md-text-field">
            <select 
              id="member-gender"
              value={isEditing ? editingMember?.gender : newMember.gender}
              onChange={(e) => {
                const gender = e.target.value as 'male' | 'female';
                if (isEditing && editingMember) {
                  setEditingMember({...editingMember, gender});
                } else {
                  setNewMember({...newMember, gender});
                }
              }}
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
            <label htmlFor="member-gender">Gender</label>
          </div>
          
          <div className="md-text-field">
            <input 
              type="number" 
              id="member-birth-year"
              value={isEditing ? editingMember?.birthYear : newMember.birthYear}
              onChange={(e) => {
                const birthYear = parseInt(e.target.value);
                if (isEditing && editingMember) {
                  setEditingMember({...editingMember, birthYear});
                } else {
                  setNewMember({...newMember, birthYear});
                }
              }}
            />
            <label htmlFor="member-birth-year">Birth Year</label>
          </div>
          
          <div className="md-text-field">
            <select 
              id="member-status"
              value={(isEditing ? editingMember?.isAlive : newMember.isAlive) ? 'alive' : 'deceased'}
              onChange={(e) => {
                const isAlive = e.target.value === 'alive';
                if (isEditing && editingMember) {
                  setEditingMember({...editingMember, isAlive});
                } else {
                  setNewMember({...newMember, isAlive});
                }
              }}
            >
              <option value="alive">Alive</option>
              <option value="deceased">Deceased</option>
            </select>
            <label htmlFor="member-status">Status</label>
          </div>
          
          {(isEditing ? !editingMember?.isAlive : !newMember.isAlive) && (
            <div className="md-text-field">
              <input 
                type="number" 
                id="member-death-year"
                value={isEditing && editingMember ? editingMember.deathYear || '' : newMember.deathYear || ''}
                onChange={(e) => {
                  const deathYear = e.target.value ? parseInt(e.target.value) : undefined;
                  if (isEditing && editingMember) {
                    setEditingMember({...editingMember, deathYear});
                  } else {
                    setNewMember({...newMember, deathYear});
                  }
                }}
              />
              <label htmlFor="member-death-year">Death Year</label>
            </div>
          )}
          
          <div className="md:col-span-2">
            <label className="block md-body-medium mb-2 parents-label">Parents</label>
            <select 
              multiple
              id="member-parents"
              className="parents-select w-full border rounded-lg h-32"
              value={isEditing && editingMember ? editingMember.parentIds.map(String) : newMember.parentIds.map(String)}
              onChange={(e) => {
                const parentIds = Array.from(e.target.selectedOptions).map(option => parseInt(option.value));
                if (isEditing && editingMember) {
                  setEditingMember({...editingMember, parentIds});
                } else {
                  setNewMember({...newMember, parentIds});
                }
              }}
            >
              {members.map(member => (
                <option key={member.id} value={member.id}>
                  {member.name} ({member.gender}, {member.birthYear})
                </option>
              ))}
            </select>
            <p className="md-body-small mt-2">Hold Ctrl/Cmd to select multiple parents</p>
          </div>
        </div>
        
        <div className="flex justify-end mt-6 button-group">
          {isEditing ? (
            <>
              <button 
                onClick={cancelEdit}
                className="md-button md-button-outlined"
              >
                Cancel
              </button>
              <button 
                onClick={saveEditMember}
                className="md-button md-button-filled"
              >
                Save Changes
              </button>
            </>
          ) : (
            <button 
              onClick={addMember}
              className="md-button md-button-filled"
            >
              Add Member
            </button>
          )}
        </div>
      </div>
      
      {/* Create Relationship Section */}
      <div className="bg-gray-100 p-6 rounded-lg mb-8 section">
        <h2 className="md-title-large mb-6">Create Relationship</h2>
        <div className="flex flex-wrap items-start">
          <div className="mb-3 mr-6">
            <label className="block md-body-medium mb-2">Relationship Type</label>
            <select 
              className="enhanced-select p-3 rounded-lg min-w-[200px]"
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value as Relationship['type'])}
            >
              <option value="marriage">Marriage</option>
              <option value="engagement">Engagement</option>
              <option value="separation">Separation</option>
              <option value="divorce">Divorce</option>
            </select>
          </div>
          
          <div className="flex-1 mb-3 flex flex-col">
            <label className="block md-body-medium mb-2">Select 2 Family Members</label>
            <div className="flex flex-wrap mb-3">
              {selectedMembers.length > 0 && (
                selectedMembers.map(id => {
                  const member = members.find(m => m.id === id);
                  return member ? (
                    <div key={id} className="bg-blue-100 rounded-lg px-4 py-2 mr-3 mb-3">
                      {member.name}
                      <button 
                        onClick={() => toggleSelectMember(id)}
                        className="ml-3 text-gray-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : null;
                })
              )}
            </div>
            
            <div className="mb-3">
              <button 
                onClick={createRelationship}
                disabled={selectedMembers.length !== 2}
                className={`md-button ${selectedMembers.length === 2 ? 'md-button-filled' : ''}`}
              >
                Create Relationship
              </button>
            </div>
            
            {selectedMembers.length === 0 && (
              <p className="md-body-medium mt-1">No members selected. Click on member cards to select.</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Family Members Display */}
      {viewMode === 'cards' ? (
        <div className="section">
          <h2 className="md-title-large mb-6">Family Members</h2>
          <div className="card-grid">
            {members.map(member => (
              <MemberCard 
                key={member.id} 
                member={member} 
                isSelected={selectedMembers.includes(member.id)}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="tree-view section">
          <h2 className="md-title-large mb-6 text-center">Family Tree</h2>
          <CanvasTreeView familyUnits={buildFamilyTree()} />
        </div>
      )}
    </div>
  );
};

export default FamilyTreeBuilder;
